"use client"

import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Bell } from "lucide-react"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { NotificationsModal } from "@/components/ui/notifications/notifications-modal"

interface AssessmentHeaderProps {
  userName?: string
  userAvatar?: string
  onNotificationClick?: () => void
}

const AssessmentHeader = React.forwardRef<HTMLDivElement, AssessmentHeaderProps>(
  ({ userName = "User", userAvatar, onNotificationClick }, ref) => {
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
    const navigate = useNavigate()

    // Mock function to handle marking notifications as read
    const handleMarkAsRead = (notificationId: string) => {
      console.log("Marking notification as read:", notificationId)
      // Implement your logic to mark notification as read
      if (onNotificationClick) {
        onNotificationClick()
      }
    }

    const handleMarkAllAsRead = () => {
      console.log("Marking all notifications as read")
      // Implement your logic to mark all notifications as read
    }

    const handleNotificationClick = () => {
      setIsNotificationsOpen(true)
      if (onNotificationClick) {
        onNotificationClick()
      }
    }

    const handleAvatarClick = () => {
      navigate("/profile")
    }

    return (
      <>
        <div ref={ref} className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Assessments</h1>
            <p className="text-slate-600 mt-1">Discover your interests and skills!</p>
          </div>

          <div className="flex items-center gap-4 mt-4 sm:mt-0">
            <button
              onClick={handleNotificationClick}
              className="relative p-2 text-slate-600 hover:text-blue-600 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="View notifications"
            >
              <Bell className="w-6 h-6" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
            </button>

            {/* Avatar with Profile Routing */}
            <button
              onClick={handleAvatarClick}
              className="transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
              aria-label="Go to profile"
            >
              <Avatar className="w-10 h-10 border-2 border-blue-600 cursor-pointer">
                <AvatarImage src={userAvatar || "/placeholder.svg"} alt={userName} />
                <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                  {userName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </div>
        </div>

        <NotificationsModal
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          position="top-right"
        />
      </>
    )
  },
)

AssessmentHeader.displayName = "AssessmentHeader"

export { AssessmentHeader }